import React from 'react';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import { Truck, Package, RotateCcw, Globe } from 'lucide-react';

const Shipping = () => {
  return (
    <div>
      <PageHeader
        eyebrow="Shipping & Returns"
        title="Free shipping. Free returns. Every order."
        intro="Every order is hand-packed at our Paris atelier and shipped with care. If anything is not quite right, we will make it right."
      />

      {/* Service pillars */}
      <section className="py-16 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-4 gap-8">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 100}>
              <div className="border border-neutral-200 p-8 h-full bg-white">
                <div className="w-10 h-10 rounded-full bg-neutral-900 text-white flex items-center justify-center mb-5">
                  <p.icon className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg font-medium mb-2">{p.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Shipping table */}
      <section className="py-16 px-6 lg:px-12 bg-[#fafaf8]">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-10">Shipping rates</h2>
          </Reveal>
          <Reveal delay={140}>
            <div className="bg-white">
              <div className="grid grid-cols-[1.4fr_1.2fr_1fr] gap-6 px-6 py-4 text-[11px] tracking-[0.22em] uppercase text-neutral-500 border-b border-neutral-200">
                <span>Destination</span>
                <span>Delivery Time</span>
                <span className="text-right">Cost</span>
              </div>
              {RATES.map((r) => (
                <div
                  key={r.dest}
                  className="grid grid-cols-[1.4fr_1.2fr_1fr] gap-6 px-6 py-5 text-sm border-b border-neutral-200 last:border-b-0 items-center"
                >
                  <span className="font-medium">{r.dest}</span>
                  <span className="text-neutral-600 tabular-nums">{r.timing}</span>
                  <span className="text-right tabular-nums">{r.cost}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Returns */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-6">
              Returns &amp; exchanges
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <div className="space-y-5 text-[15px] leading-relaxed text-neutral-600">
              <p>
                We offer <span className="text-neutral-900">free returns within 30 days</span> of delivery on
                full-priced items. Sale items are eligible for exchange or store credit only.
              </p>
              <p>
                To begin a return, log in to your account or email{' '}
                <a href="mailto:andobreeofficial1@gmail.com" className="text-neutral-900 underline underline-offset-4">
                  andobreeofficial1@gmail.com
                </a>
                . We will send a pre-paid label by the next morning.
              </p>
              <p>
                Pieces must be returned in their original condition, with all tags attached. Refunds are
                processed within five business days of arrival.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const PILLARS = [
  { icon: Truck, title: 'Free Shipping', body: 'Complimentary standard shipping on every order, no minimum.' },
  { icon: Package, title: 'Hand-Packed', body: 'Every piece is wrapped in our signature linen and boxed by hand.' },
  { icon: RotateCcw, title: 'Free Returns', body: '30 days to decide. Pre-paid labels on us, refunds in 5 days.' },
  { icon: Globe, title: 'Carbon-Neutral', body: 'All shipments offset through certified reforestation partners.' },
];

const RATES = [
  { dest: 'France', timing: '2\u20133 business days', cost: 'Free' },
  { dest: 'European Union', timing: '3\u20135 business days', cost: 'Free' },
  { dest: 'United Kingdom', timing: '3\u20135 business days', cost: 'Free over $300 \u00b7 $20' },
  { dest: 'United States / Canada', timing: '4\u20137 business days', cost: 'Free over $500 \u00b7 $35' },
  { dest: 'Rest of World', timing: '5\u201310 business days', cost: 'Calculated at checkout' },
];

export default Shipping;
